import type { ComponentType } from "react";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react/ArrowsClockwise";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/Clock";
import { CopyIcon } from "@phosphor-icons/react/Copy";
import { SpinnerGapIcon } from "@phosphor-icons/react/SpinnerGap";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import { useConnectivityTests } from "./Hooks/useConnectivityTests";
import {
  formatResultsForCopy,
  overallStatusBannerVariant,
  overallStatusText,
} from "./Helpers/connectivityTestTypes";
import type { TestStatus } from "./Helpers/connectivityTestTypes";

const STATUS_META: Record<
  TestStatus,
  {
    variant: "success" | "warning" | "danger" | "info" | "neutral";
    icon: ComponentType<{ className?: string }>;
  }
> = {
  passed: { variant: "success", icon: CheckCircleIcon },
  failed: { variant: "danger", icon: XCircleIcon },
  warning: { variant: "warning", icon: WarningIcon },
  running: { variant: "info", icon: SpinnerGapIcon },
  pending: { variant: "neutral", icon: ClockIcon },
};

function ConnectivityTestContent_() {
  const { results, isRunning, isCopied, runAllTests, copyResults } =
    useConnectivityTests();

  return (
    <Stack gap={6}>
      <Stack gap={3}>
        <Heading level={2}>
          FreightSimple Dashboard Connectivity Diagnostic
        </Heading>
        <Banner variant="info" title="What this page does">
          <T>
            This diagnostic tool tests your connection to FreightSimple
            Dashboard and related services. It helps identify whether
            connectivity issues are on your end or with our servers. Run the
            tests and share the results with support if you&apos;re experiencing
            problems.
          </T>
        </Banner>
      </Stack>

      <Row>
        <Button
          primary
          size="large"
          loading={isRunning}
          disabled={isRunning}
          disabledMessage="Tests are running"
          icon={ArrowsClockwiseIcon}
          onClick={runAllTests}
        >
          {isRunning ? "Running tests…" : "Run connectivity tests"}
        </Button>
      </Row>

      {results && (
        <Stack gap={4}>
          {!isRunning && (
            <Banner
              variant={overallStatusBannerVariant(results.overallStatus)}
              title={overallStatusText(results.overallStatus)}
              actions={
                <Button size="compact" icon={CopyIcon} onClick={copyResults}>
                  {isCopied ? "Copied!" : "Copy results"}
                </Button>
              }
            >
              <T size="sm" color="secondary">
                {results.overallStatus === "ok"
                  ? "All connectivity checks passed."
                  : "Share these results with support if problems persist."}
              </T>
            </Banner>
          )}

          <Panel>
            <Stack gap={4}>
              {results.tests.map((test) => {
                const meta = STATUS_META[test.status];
                return (
                  <Stack gap={1} key={test.name}>
                    <Row justify="between" align="center">
                      <Row gap={2} align="baseline">
                        <T weight="semibold">{test.name}</T>
                        {test.duration !== undefined && (
                          <T size="sm" color="muted">
                            {test.duration}ms
                          </T>
                        )}
                      </Row>
                      <Badge
                        variant={meta.variant}
                        icon={meta.icon}
                        label={test.status}
                      />
                    </Row>
                    {test.details && (
                      <T size="sm" color="secondary">
                        {test.details}
                      </T>
                    )}
                    {test.error && (
                      <T size="sm" color="red">
                        Error: {test.error}
                      </T>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Panel>

          <Panel>
            <Stack gap={2}>
              <Row justify="between" align="start">
                <Stack gap={0.5}>
                  <T weight="semibold">Copyable results</T>
                  <T size="sm" color="muted">
                    Copy this text to share with support teams
                  </T>
                </Stack>
                <Button size="compact" icon={CopyIcon} onClick={copyResults}>
                  {isCopied ? "Copied!" : "Copy to clipboard"}
                </Button>
              </Row>
              <T size="sm" mono multiline>
                {formatResultsForCopy(results)}
              </T>
            </Stack>
          </Panel>
        </Stack>
      )}
    </Stack>
  );
}

export default function ConnectivityTestScreen() {
  return (
    <FullWidthLayout
      header="Connectivity Test"
      microcopy="Diagnose your connection to FreightSimple and related services"
      content={<ConnectivityTestContent_ />}
    />
  );
}
